import React, { useState, useCallback, useEffect, useRef } from "react";
import { Loader2, CheckCircle2, Building2, Upload, Trash2, AlertCircle } from "lucide-react";
import { publicClient } from "@/lib/apiClient";
import { isImageFile, formatBytes } from "@/lib/imageUtils";
import {
  PROPERTY_TYPES,
  SERVICE_NEEDED_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  OCCUPIED_OPTIONS,
  GC_OR_DIVISION_OPTIONS,
} from "@/data/commercialContent";

const FILE_MAX_BYTES = 3 * 1024 * 1024;
const MAX_FILES = 5;

const INITIAL = {
  company_name: "",
  contact_name: "",
  phone: "",
  email: "",
  property_address: "",
  city: "",
  property_type: "Office",
  service_needed: "Commercial remodel",
  project_type: "Full GC coordination",
  timeline: "",
  occupied_status: "Occupied",
  gc_or_division: "Full GC coordination",
  message: "",
  files: [],
  website: "",
};

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/**
 * CommercialInquiryForm
 *
 * Renders on /commercial. Submits to POST /api/commercial-inquiries with up
 * to 5 base64-embedded files (plans, photos, scope docs).
 */
export default function CommercialInquiryForm() {
  const [form, setForm] = useState(INITIAL);
  const [state, setState] = useState({ loading: false, success: false, error: "" });
  const inputRef = useRef(null);

  // Reset success state if the user starts editing again after a submit.
  useEffect(() => {
    if (state.success) return;
  }, [state.success]);

  const update = useCallback((k) => (e) => {
    const v = e?.target?.value ?? e;
    setForm((p) => ({ ...p, [k]: v }));
  }, []);

  const handleFiles = useCallback(async (e) => {
    const incoming = Array.from(e.target.files || []);
    if (incoming.length === 0) return;
    const remaining = MAX_FILES - form.files.length;
    const accepted = incoming.slice(0, remaining);
    const errors = [];
    const next = [...form.files];
    for (const f of accepted) {
      if (f.size > FILE_MAX_BYTES) {
        errors.push(`${f.name}: exceeds 3MB.`);
        continue;
      }
      try {
        const dataUrl = await readAsDataUrl(f);
        next.push({
          filename: f.name,
          content_type: f.type || "application/octet-stream",
          size: f.size,
          data_url: dataUrl,
          label: isImageFile(f) ? "photo" : "document",
        });
      } catch {
        errors.push(`${f.name}: failed to read.`);
      }
    }
    setForm((p) => ({ ...p, files: next }));
    if (errors.length) setState({ loading: false, success: false, error: errors.join(" ") });
    if (inputRef.current) inputRef.current.value = "";
  }, [form.files]);

  const removeFile = (idx) => setForm((p) => ({ ...p, files: p.files.filter((_, i) => i !== idx) }));

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.phone || !form.email) {
      setState({ loading: false, success: false, error: "Contact name, phone, and email are required." });
      return;
    }
    setState({ loading: true, success: false, error: "" });
    try {
      await publicClient.post("/commercial-inquiries", form);
      setState({ loading: false, success: true, error: "" });
      setForm(INITIAL);
    } catch (err) {
      setState({
        loading: false,
        success: false,
        error: err?.response?.data?.detail || "Something went wrong, please try again.",
      });
    }
  }, [form]);

  if (state.success) {
    return (
      <div id="commercial-form" className="bg-white border border-gray-200 rounded-lg p-6 scroll-mt-24" data-testid="commercial-inquiry-success">
        <div className="flex items-start gap-3 bg-br-paper border border-br-bronze rounded p-5">
          <CheckCircle2 size={22} className="text-br-bronze shrink-0 mt-0.5" />
          <div>
            <div className="font-display tracking-widest uppercase text-lg text-br-black">Request Received</div>
            <p className="text-sm text-gray-700 mt-1 leading-relaxed">
              Thank you for contacting Black Ridge Construction about your commercial project. We received your request and will follow up to review the scope.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="commercial-form" className="bg-white border border-gray-200 rounded-lg overflow-hidden scroll-mt-24" data-testid="commercial-inquiry-form">
      <div className="px-6 py-5 border-b border-gray-200 bg-br-paper flex items-start gap-3">
        <Building2 size={20} className="text-br-bronze shrink-0 mt-1" />
        <div>
          <div className="br-eyebrow text-br-bronze">Commercial</div>
          <h3 className="font-display text-2xl tracking-widest uppercase mt-1">Request a Commercial Consultation</h3>
          <p className="text-sm text-gray-700 mt-2 max-w-2xl">
            Tell us about your property and scope. We&rsquo;ll review and follow up to coordinate a walkthrough, quote, or partner conversation depending on what fits the project.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="p-6 grid sm:grid-cols-2 gap-4" data-testid="commercial-form-fields">
        <div>
          <label className="label-br" htmlFor="cif-company">Company Name</label>
          <input id="cif-company" className="input-br" value={form.company_name} onChange={update("company_name")} data-testid="cif-company" />
        </div>
        <div>
          <label className="label-br" htmlFor="cif-contact">Contact Name *</label>
          <input id="cif-contact" className="input-br" value={form.contact_name} onChange={update("contact_name")} required data-testid="cif-contact" />
        </div>
        <div>
          <label className="label-br" htmlFor="cif-phone">Phone *</label>
          <input id="cif-phone" type="tel" className="input-br" value={form.phone} onChange={update("phone")} required data-testid="cif-phone" />
        </div>
        <div>
          <label className="label-br" htmlFor="cif-email">Email *</label>
          <input id="cif-email" type="email" className="input-br" value={form.email} onChange={update("email")} required data-testid="cif-email" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-br" htmlFor="cif-address">Property Address</label>
          <input id="cif-address" className="input-br" value={form.property_address} onChange={update("property_address")} data-testid="cif-address" />
        </div>
        <div>
          <label className="label-br" htmlFor="cif-city">City / Service Area</label>
          <input id="cif-city" className="input-br" value={form.city} onChange={update("city")} placeholder="Austin, Round Rock, Cedar Park…" data-testid="cif-city" />
        </div>
        <div>
          <label className="label-br" htmlFor="cif-property-type">Property Type</label>
          <select id="cif-property-type" className="select-br" value={form.property_type} onChange={update("property_type")} data-testid="cif-property-type">
            {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="label-br" htmlFor="cif-service">Service Needed</label>
          <select id="cif-service" className="select-br" value={form.service_needed} onChange={update("service_needed")} data-testid="cif-service">
            {SERVICE_NEEDED_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="label-br" htmlFor="cif-project-type">Project Type</label>
          <select id="cif-project-type" className="select-br" value={form.project_type} onChange={update("project_type")} data-testid="cif-project-type">
            {PROJECT_TYPE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="label-br" htmlFor="cif-timeline">Timeline</label>
          <input id="cif-timeline" className="input-br" value={form.timeline} onChange={update("timeline")} placeholder="e.g. Within 30 days, Q2, flexible…" data-testid="cif-timeline" />
        </div>
        <div>
          <label className="label-br" htmlFor="cif-occupied">Occupied or Vacant</label>
          <select id="cif-occupied" className="select-br" value={form.occupied_status} onChange={update("occupied_status")} data-testid="cif-occupied">
            {OCCUPIED_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="label-br" htmlFor="cif-gc">Full GC or Single Division</label>
          <select id="cif-gc" className="select-br" value={form.gc_or_division} onChange={update("gc_or_division")} data-testid="cif-gc">
            {GC_OR_DIVISION_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className="label-br" htmlFor="cif-message">Message / Scope Notes</label>
          <textarea id="cif-message" rows={4} className="textarea-br" value={form.message} onChange={update("message")} data-testid="cif-message" placeholder="Brief overview of the work — access notes, known constraints, budget range, etc." />
        </div>

        {/* Uploads */}
        <div className="sm:col-span-2">
          <div className="label-br mb-1">Plans / Photos / Scope Documents</div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-br-paper/40 p-5">
            <div className="flex items-center gap-3">
              <Upload size={20} className="text-br-bronze" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900">Drop or browse files</div>
                <div className="text-[11px] text-gray-500">Up to {MAX_FILES} files, 3MB each. PDFs or images preferred.</div>
              </div>
              <button type="button" onClick={() => inputRef.current?.click()} className="br-btn-dark text-[11px]" disabled={form.files.length >= MAX_FILES} data-testid="cif-file-pick">
                Browse
              </button>
              <input ref={inputRef} type="file" multiple className="hidden" onChange={handleFiles} accept=".pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" data-testid="cif-file-input" />
            </div>
            {form.files.length > 0 && (
              <ul className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="cif-file-list">
                {form.files.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-700 bg-white border border-gray-100 rounded px-2 py-1.5">
                    <Building2 size={12} className="text-br-bronze shrink-0" />
                    <span className="truncate flex-1">{f.filename}</span>
                    <span className="text-[10px] text-gray-400">{formatBytes(f.size)}</span>
                    <button type="button" onClick={() => removeFile(i)} aria-label="Remove" className="text-red-500 hover:text-red-700">
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Honeypot */}
        <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} className="hidden" aria-hidden="true" />

        {state.error && (
          <div className="sm:col-span-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 flex items-start gap-2" data-testid="cif-error">
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> {state.error}
          </div>
        )}

        <div className="sm:col-span-2 flex flex-wrap items-center gap-3 pt-1">
          <button type="submit" disabled={state.loading} className="br-btn-primary" data-testid="cif-submit">
            {state.loading ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : "Send Commercial Request"}
          </button>
          <span className="text-[11px] text-gray-500">Form submissions land in the Black Ridge admin dashboard.</span>
        </div>
      </form>
    </div>
  );
}
